import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, Clock, Send, CheckCircle, ArrowRight, ArrowLeft,
  User, Home, FileText, Camera, AlertCircle, Plus, Minus, Ruler, Calendar
} from 'lucide-react';
import { companyInfo, formOptions } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { AddressAutocomplete } from '../ui/address-autocomplete';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import SEO, { seoData } from '../seo/SEO';

const ContactPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1 - Client info
    clientType: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    postalCode: '',
    city: '',
    // Step 2 - Request type
    requestType: '',
    serviceType: '',
    // Step 3 - Details
    description: '',
    isUrgent: false,
    preferredDate: '',
    // Step 4 - Access
    accessType: '',
    housingType: '',
    isPresent: true,
    // Step 5 - Validation
    consent: false,
    // Renovation options (conditional)
    renovationPropertyType: '',
    renovationPropertyTypeOther: '',
    renovationMaterialType: '',
    renovationInstallType: '',
    renovationFenetres: 0,
    renovationPortesFenetres: 0,
    renovationPorteEntree: 0,
    renovationAutreElement: '',
    renovationKnowsDimensions: false,
    renovationDimensions: '',
    renovationTimeline: ''
  });

  const totalSteps = 5;
  const isRenovationRequest = formData.requestType === 'renovation';
  
  const stepTitles = [
    "Vos coordonnées",
    "Type de demande",
    "Détails",
    "Lieu d'intervention",
    "Validation"
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Counter component for renovation elements
  const CounterInput = ({ label, value, onChange, min = 0, max = 20 }) => (
    <div className="flex items-center justify-between p-3 bg-[hsl(210,20%,98%)] rounded-lg">
      <span className="text-sm font-medium text-[hsl(215,25%,15%)]">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
          disabled={value <= min}
        >
          <Minus className="w-4 h-4 text-gray-600" />
        </button>
        <span className="w-8 text-center font-semibold text-[hsl(215,25%,15%)]">{value}</span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + 1))}
          className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
          disabled={value >= max}
        >
          <Plus className="w-4 h-4 text-gray-600" />
        </button>
      </div>
    </div>
  );

  const nextStep = () => {
    if (currentStep < totalSteps) {
      const nextStepNum = currentStep + 1;
      setCurrentStep(nextStepNum);
      
      // GA4 - Suivi de progression du formulaire
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'form_progress', {
          event_category: 'Contact Form',
          event_label: stepTitles[nextStepNum - 1] || `Étape ${nextStepNum}`,
          form_step: nextStepNum,
          request_type: formData.requestType || 'non_défini'
        });
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        // Gestion des erreurs de validation Pydantic (detail peut être un objet ou un tableau)
        let errorMessage = 'Erreur lors de l\'envoi de la demande';
        
        // Dictionnaire de traduction des erreurs Pydantic
        const errorTranslations = {
          'String should have at least 10 characters': 'La description doit contenir au moins 10 caractères',
          'String should have at least 2 characters': 'Le nom doit contenir au moins 2 caractères',
          'value is not a valid email address': 'L\'adresse email n\'est pas valide',
          'Field required': 'Ce champ est requis',
          'Input should be': 'Valeur incorrecte pour ce champ'
        };
        
        const translateError = (msg) => {
          for (const [key, value] of Object.entries(errorTranslations)) {
            if (msg && msg.includes(key)) {
              return value;
            }
          }
          return msg;
        };
        
        if (typeof errorData.detail === 'string') {
          errorMessage = translateError(errorData.detail);
        } else if (Array.isArray(errorData.detail)) {
          // Erreur de validation Pydantic - extraire et traduire le premier message
          const rawMsg = errorData.detail[0]?.msg || errorData.detail[0]?.message;
          errorMessage = translateError(rawMsg) || errorMessage;
        } else if (typeof errorData.detail === 'object' && errorData.detail !== null) {
          const rawMsg = errorData.detail.msg || errorData.detail.message;
          errorMessage = translateError(rawMsg) || JSON.stringify(errorData.detail);
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('Contact request created:', result);
      
      // Google Analytics 4 - Événement de conversion
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'Contact',
          event_label: formData.requestType === 'renovation' ? 'Demande Rénovation' : 'Demande Standard',
          currency: 'EUR',
          value: formData.requestType === 'renovation' ? 500 : 100,
          // Paramètres personnalisés pour l'analyse
          client_type: formData.clientType,
          request_type: formData.requestType,
          service_type: formData.serviceType || 'renovation',
          is_urgent: formData.isUrgent,
          city: formData.city || 'Non spécifié'
        });
        console.log('GA4 conversion event sent: generate_lead');
      }
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setIsSubmitting(false);
      toast.error(error.message || 'Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.clientType && formData.name && formData.phone && formData.email;
      case 2:
        // Pour rénovation, le service est pré-défini comme "menuiserie"
        if (isRenovationRequest) {
          return true; // Les options rénovation sont facultatives
        }
        return formData.requestType && formData.serviceType;
      case 3:
        // La description doit avoir au moins 10 caractères
        return formData.description && formData.description.length >= 10;
      case 4:
        return formData.accessType && formData.housingType;
      case 5:
        return formData.consent;
      default:
        return false;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[hsl(210,20%,98%)] py-20">
        <SEO 
          title="Demande envoyée | NGL SERVICES"
          description="Votre demande a été envoyée avec succès. Un technicien NGL SERVICES vous contactera rapidement."
          noindex={true}
        />
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto border-0 shadow-xl">
            <CardContent className="p-10 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-4">
                Merci pour votre demande !
              </h2>
              <p className="text-muted-foreground mb-8">
                Un technicien NGL SERVICES vous contactera rapidement.
                <br />
                Pour toute urgence, appelez le <strong>{companyInfo.phoneEmergency}</strong>.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/">
                  <Button className="btn-primary rounded-full">
                    Retour à l'accueil
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phoneEmergency}`}>
                  <Button variant="outline" className="rounded-full">
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler maintenant
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Contact page Schema.org structured data
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact NGL SERVICES",
    "description": seoData.contact.description,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": companyInfo.name,
      "telephone": companyInfo.phone,
      "email": companyInfo.email,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Villiers-sur-Marne",
        "postalCode": "94350",
        "addressCountry": "FR"
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO 
        title={seoData.contact.title}
        description={seoData.contact.description}
        keywords={seoData.contact.keywords}
        canonical="/contact"
        schemaData={contactSchema}
      />

      {/* Hero Section */}
      <section className="relative py-16 bg-[hsl(207,78%,42%)]">
        <div className="container mx-auto px-4 relative z-10">
          <Breadcrumb className="mb-6">
            <BreadcrumbList className="text-white/70">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">Contact</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contactez-nous
            </h1>
            <p className="text-xl text-white/90">
              Réponse rapide – Intervention possible le jour même
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  {/* Progress bar */}
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                      {[1, 2, 3, 4, 5].map((step) => (
                        <div key={step} className="flex flex-col items-center">
                          <div className={`step-indicator ${
                            step === currentStep ? 'active' : 
                            step < currentStep ? 'completed' : 'pending'
                          }`}>
                            {step < currentStep ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : step}
                          </div>
                          <span className="text-xs mt-2 text-muted-foreground hidden sm:block">
                            {stepTitles[step - 1]}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-full bg-[hsl(207,78%,42%)] rounded-full transition-all duration-300"
                        style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Step 1 - Client Info */}
                  {currentStep === 1 && (
                    <div className="animate-fade-in">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center">
                          <User className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                        </div>
                        <h2 className="text-xl font-bold text-[hsl(215,25%,15%)]">
                          Vos coordonnées
                        </h2>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {/* Type de client */}
                        <div className="sm:col-span-2">
                          <Label>Vous êtes *</Label>
                          <Select value={formData.clientType} onValueChange={(v) => updateFormData('clientType', v)}>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Sélectionnez votre profil" />
                            </SelectTrigger>
                            <SelectContent>
                              {formOptions.clientTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="sm:col-span-2">
                          <Label htmlFor="name">
                            {formData.clientType === 'professionnel' || formData.clientType === 'association' || formData.clientType === 'immobilier' 
                              ? 'Nom de la société / Organisation *' 
                              : 'Nom complet *'}
                          </Label>
                          <Input 
                            id="name" 
                            value={formData.name}
                            onChange={(e) => updateFormData('name', e.target.value)}
                            placeholder={formData.clientType === 'professionnel' ? "Nom de votre entreprise" : "Jean Dupont"}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone">Téléphone *</Label>
                          <Input 
                            id="phone" 
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => updateFormData('phone', e.target.value)}
                            placeholder="06 12 34 56 78"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input 
                            id="email" 
                            type="email"
                            value={formData.email}
                            onChange={(e) => updateFormData('email', e.target.value)}
                            placeholder="contact@email.com"
                            className="mt-1"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label>Adresse d'intervention</Label>
                          <div className="mt-1">
                            <AddressAutocomplete
                              initialValue={formData.address ? `${formData.address}, ${formData.postalCode} ${formData.city}`.trim() : ''}
                              placeholder="Commencez à taper votre adresse..."
                              onAddressSelect={(selected) => {
                                updateFormData('address', selected.address);
                                updateFormData('postalCode', selected.postalCode);
                                updateFormData('city', selected.city);
                              }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Tapez au moins 3 caractères pour voir les suggestions
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="postalCode">Code postal</Label>
                          <Input 
                            id="postalCode" 
                            value={formData.postalCode}
                            onChange={(e) => updateFormData('postalCode', e.target.value)}
                            placeholder="94350"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="city">Ville</Label>
                          <Input 
                            id="city" 
                            value={formData.city}
                            onChange={(e) => updateFormData('city', e.target.value)}
                            placeholder="Villiers-sur-Marne"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2 - Request Type */}
                  {currentStep === 2 && (
                    <div className="animate-fade-in">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                        </div>
                        <h2 className="text-xl font-bold text-[hsl(215,25%,15%)]">
                          Type de demande
                        </h2>
                      </div>
                      <div className="space-y-6">
                        <div>
                          <Label className="mb-3 block">Nature de la demande *</Label>
                          <RadioGroup 
                            value={formData.requestType} 
                            onValueChange={(v) => updateFormData('requestType', v)}
                            className="grid sm:grid-cols-2 gap-3"
                          >
                            {formOptions.requestTypes.map((type) => (
                              <div key={type.value}>
                                <RadioGroupItem 
                                  value={type.value} 
                                  id={type.value}
                                  className="peer sr-only"
                                />
                                <Label 
                                  htmlFor={type.value}
                                  className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all text-center ${
                                    formData.requestType === type.value 
                                      ? 'border-[hsl(207,78%,42%)] bg-[hsl(207,78%,95%)]' 
                                      : 'border-gray-200 hover:border-gray-300'
                                  } ${type.value === 'renovation' ? 'sm:col-span-2 bg-gradient-to-r from-[hsl(207,78%,97%)] to-[hsl(24,95%,97%)] border-[hsl(24,95%,70%)]' : ''}`}
                                >
                                  {type.label}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>

                        {/* Options Rénovation - Affichage conditionnel */}
                        {isRenovationRequest && (
                          <div className="space-y-6 p-6 bg-gradient-to-br from-[hsl(207,78%,97%)] to-[hsl(24,95%,98%)] rounded-xl border border-[hsl(24,95%,85%)]">
                            <div className="flex items-center gap-2 mb-4">
                              <Home className="w-5 h-5 text-[hsl(24,95%,53%)]" />
                              <h3 className="font-semibold text-[hsl(215,25%,15%)]">
                                Informations sur votre projet de rénovation
                              </h3>
                            </div>
                            
                            {/* 1. Type de bien */}
                            <div>
                              <Label className="mb-2 block">Type de bien</Label>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {formOptions.renovationPropertyTypes.map((type) => (
                                  <label
                                    key={type.value}
                                    className={`flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-all text-sm ${
                                      formData.renovationPropertyType === type.value
                                        ? 'border-[hsl(207,78%,42%)] bg-white'
                                        : 'border-gray-200 hover:border-gray-300 bg-white/50'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="renovationPropertyType"
                                      value={type.value}
                                      checked={formData.renovationPropertyType === type.value}
                                      onChange={(e) => updateFormData('renovationPropertyType', e.target.value)}
                                      className="sr-only"
                                    />
                                    {type.label}
                                  </label>
                                ))}
                              </div>
                              {formData.renovationPropertyType === 'autre' && (
                                <Input
                                  placeholder="Précisez le type de bien..."
                                  value={formData.renovationPropertyTypeOther}
                                  onChange={(e) => updateFormData('renovationPropertyTypeOther', e.target.value)}
                                  className="mt-2"
                                />
                              )}
                            </div>

                            {/* 2. Type de menuiserie */}
                            <div>
                              <Label className="mb-2 block">Type de menuiserie souhaitée</Label>
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                {formOptions.renovationMaterialTypes.map((type) => (
                                  <label
                                    key={type.value}
                                    className={`flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-all text-sm ${
                                      formData.renovationMaterialType === type.value
                                        ? 'border-[hsl(207,78%,42%)] bg-white'
                                        : 'border-gray-200 hover:border-gray-300 bg-white/50'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="renovationMaterialType"
                                      value={type.value}
                                      checked={formData.renovationMaterialType === type.value}
                                      onChange={(e) => updateFormData('renovationMaterialType', e.target.value)}
                                      className="sr-only"
                                    />
                                    {type.label}
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* 3. Type de pose */}
                            <div>
                              <Label className="mb-2 block">Type de pose</Label>
                              <div className="grid gap-2">
                                {formOptions.renovationInstallTypes.map((type) => (
                                  <label
                                    key={type.value}
                                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all text-sm ${
                                      formData.renovationInstallType === type.value
                                        ? 'border-[hsl(207,78%,42%)] bg-white'
                                        : 'border-gray-200 hover:border-gray-300 bg-white/50'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="renovationInstallType"
                                      value={type.value}
                                      checked={formData.renovationInstallType === type.value}
                                      onChange={(e) => updateFormData('renovationInstallType', e.target.value)}
                                      className="sr-only"
                                    />
                                    {type.label}
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* 4. Nombre d'éléments */}
                            <div>
                              <Label className="mb-3 block">Nombre d'éléments à remplacer</Label>
                              <div className="space-y-2">
                                <CounterInput
                                  label="Fenêtres"
                                  value={formData.renovationFenetres}
                                  onChange={(v) => updateFormData('renovationFenetres', v)}
                                />
                                <CounterInput
                                  label="Portes-fenêtres"
                                  value={formData.renovationPortesFenetres}
                                  onChange={(v) => updateFormData('renovationPortesFenetres', v)}
                                />
                                <CounterInput
                                  label="Porte d'entrée"
                                  value={formData.renovationPorteEntree}
                                  onChange={(v) => updateFormData('renovationPorteEntree', v)}
                                  max={1}
                                />
                                <div className="mt-3">
                                  <Input
                                    placeholder="Autre élément (volets, baie vitrée, etc.)"
                                    value={formData.renovationAutreElement}
                                    onChange={(e) => updateFormData('renovationAutreElement', e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* 5. Dimensions (optionnelles) */}
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <Checkbox
                                  id="knowsDimensions"
                                  checked={formData.renovationKnowsDimensions}
                                  onCheckedChange={(checked) => updateFormData('renovationKnowsDimensions', checked)}
                                />
                                <Label htmlFor="knowsDimensions" className="cursor-pointer flex items-center gap-2">
                                  <Ruler className="w-4 h-4 text-[hsl(207,78%,42%)]" />
                                  Je connais les dimensions
                                </Label>
                              </div>
                              {formData.renovationKnowsDimensions ? (
                                <Textarea
                                  placeholder="Ex: Fenêtre cuisine: 120x100cm, Porte-fenêtre salon: 240x220cm..."
                                  value={formData.renovationDimensions}
                                  onChange={(e) => updateFormData('renovationDimensions', e.target.value)}
                                  rows={3}
                                  className="bg-white"
                                />
                              ) : (
                                <p className="text-sm text-muted-foreground bg-white/70 p-3 rounded-lg">
                                  💡 Pas d'inquiétude, nos techniciens prendront les mesures lors du rendez-vous.
                                </p>
                              )}
                            </div>

                            {/* 6. Délai souhaité */}
                            <div>
                              <Label className="mb-2 block flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-[hsl(207,78%,42%)]" />
                                Délai de réalisation souhaité
                              </Label>
                              <div className="grid grid-cols-2 gap-2">
                                {formOptions.renovationTimelines.map((type) => (
                                  <label
                                    key={type.value}
                                    className={`flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-all text-sm ${
                                      formData.renovationTimeline === type.value
                                        ? 'border-[hsl(207,78%,42%)] bg-white'
                                        : 'border-gray-200 hover:border-gray-300 bg-white/50'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="renovationTimeline"
                                      value={type.value}
                                      checked={formData.renovationTimeline === type.value}
                                      onChange={(e) => updateFormData('renovationTimeline', e.target.value)}
                                      className="sr-only"
                                    />
                                    {type.label}
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Service concerné - toujours affiché */}
                        {!isRenovationRequest && (
                          <div>
                            <Label htmlFor="serviceType">Service concerné *</Label>
                            <Select value={formData.serviceType} onValueChange={(v) => updateFormData('serviceType', v)}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Sélectionnez un service" />
                              </SelectTrigger>
                              <SelectContent>
                                {formOptions.serviceTypes.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3 - Details */}
                  {currentStep === 3 && (
                    <div className="animate-fade-in">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                        </div>
                        <h2 className="text-xl font-bold text-[hsl(215,25%,15%)]">
                          Détails de votre demande
                        </h2>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="description">Description du problème ou de la demande *</Label>
                          <Textarea 
                            id="description" 
                            value={formData.description}
                            onChange={(e) => updateFormData('description', e.target.value)}
                            placeholder="Décrivez votre besoin en détail (minimum 10 caractères)..."
                            className="mt-1 min-h-[120px]"
                          />
                          {formData.description && formData.description.length < 10 && (
                            <p className="text-xs text-[hsl(24,95%,45%)] mt-1">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              Minimum 10 caractères requis ({formData.description.length}/10)
                            </p>
                          )}
                          {formData.description && formData.description.length >= 10 && (
                            <p className="text-xs text-green-600 mt-1">
                              <CheckCircle className="w-3 h-3 inline mr-1" />
                              Description valide
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 p-4 bg-[hsl(24,95%,95%)] rounded-lg">
                          <Checkbox 
                            id="isUrgent" 
                            checked={formData.isUrgent}
                            onCheckedChange={(checked) => updateFormData('isUrgent', checked)}
                          />
                          <div>
                            <Label htmlFor="isUrgent" className="text-[hsl(24,95%,40%)] font-medium cursor-pointer">
                              <AlertCircle className="w-4 h-4 inline mr-2" />
                              C'est une urgence
                            </Label>
                            <p className="text-xs text-[hsl(24,95%,35%)]">
                              Cochez si vous avez besoin d'une intervention rapide
                            </p>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="preferredDate">Date souhaitée (si intervention programmée)</Label>
                          <Input 
                            id="preferredDate" 
                            type="date"
                            value={formData.preferredDate}
                            onChange={(e) => updateFormData('preferredDate', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 4 - Access */}
                  {currentStep === 4 && (
                    <div className="animate-fade-in">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center">
                          <Home className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                        </div>
                        <h2 className="text-xl font-bold text-[hsl(215,25%,15%)]">
                          Accès au logement
                        </h2>
                      </div>
                      <div className="space-y-6">
                        <div>
                          <Label className="mb-3 block">Type de logement *</Label>
                          <RadioGroup 
                            value={formData.housingType} 
                            onValueChange={(v) => updateFormData('housingType', v)}
                            className="grid sm:grid-cols-2 gap-3"
                          >
                            {formOptions.housingTypes.map((type) => (
                              <div key={type.value}>
                                <RadioGroupItem 
                                  value={type.value} 
                                  id={`housing-${type.value}`}
                                  className="peer sr-only"
                                />
                                <Label 
                                  htmlFor={`housing-${type.value}`}
                                  className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                    formData.housingType === type.value 
                                      ? 'border-[hsl(207,78%,42%)] bg-[hsl(207,78%,95%)]' 
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  {type.label}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                        <div>
                          <Label className="mb-3 block">Facilité d'accès *</Label>
                          <RadioGroup 
                            value={formData.accessType} 
                            onValueChange={(v) => updateFormData('accessType', v)}
                            className="grid sm:grid-cols-2 gap-3"
                          >
                            {formOptions.accessTypes.map((type) => (
                              <div key={type.value}>
                                <RadioGroupItem 
                                  value={type.value} 
                                  id={`access-${type.value}`}
                                  className="peer sr-only"
                                />
                                <Label 
                                  htmlFor={`access-${type.value}`}
                                  className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                    formData.accessType === type.value 
                                      ? 'border-[hsl(207,78%,42%)] bg-[hsl(207,78%,95%)]' 
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  {type.label}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            id="isPresent" 
                            checked={formData.isPresent}
                            onCheckedChange={(checked) => updateFormData('isPresent', checked)}
                          />
                          <Label htmlFor="isPresent" className="cursor-pointer">
                            Je serai présent(e) lors de l'intervention
                          </Label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 5 - Validation */}
                  {currentStep === 5 && (
                    <div className="animate-fade-in">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                        </div>
                        <h2 className="text-xl font-bold text-[hsl(215,25%,15%)]">
                          Récapitulatif et validation
                        </h2>
                      </div>
                      
                      {/* Summary */}
                      <div className="bg-[hsl(210,20%,98%)] rounded-lg p-5 mb-6 space-y-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Profil :</span>
                          <span className="font-medium">
                            {formOptions.clientTypes.find(t => t.value === formData.clientType)?.label || '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Nom :</span>
                          <span className="font-medium">{formData.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Téléphone :</span>
                          <span className="font-medium">{formData.phone}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Email :</span>
                          <span className="font-medium">{formData.email}</span>
                        </div>
                        {formData.address && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Adresse :</span>
                            <span className="font-medium">{formData.address}, {formData.postalCode} {formData.city}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Type de demande :</span>
                          <span className="font-medium">
                            {formOptions.requestTypes.find(t => t.value === formData.requestType)?.label || '-'}
                          </span>
                        </div>
                        
                        {/* Options spécifiques pour Rénovation */}
                        {isRenovationRequest && (
                          <>
                            {formData.renovationPropertyType && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Type de bien :</span>
                                <span className="font-medium">
                                  {formOptions.renovationPropertyTypes.find(t => t.value === formData.renovationPropertyType)?.label || formData.renovationPropertyType}
                                  {formData.renovationPropertyTypeOther && ` (${formData.renovationPropertyTypeOther})`}
                                </span>
                              </div>
                            )}
                            {formData.renovationMaterialType && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Menuiserie :</span>
                                <span className="font-medium">
                                  {formOptions.renovationMaterialTypes.find(t => t.value === formData.renovationMaterialType)?.label || formData.renovationMaterialType}
                                </span>
                              </div>
                            )}
                            {formData.renovationInstallType && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Type de pose :</span>
                                <span className="font-medium">
                                  {formOptions.renovationInstallTypes.find(t => t.value === formData.renovationInstallType)?.label || formData.renovationInstallType}
                                </span>
                              </div>
                            )}
                            {(formData.renovationFenetres > 0 || formData.renovationPortesFenetres > 0 || formData.renovationPorteEntree > 0) && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Éléments :</span>
                                <span className="font-medium">
                                  {[
                                    formData.renovationFenetres > 0 && `${formData.renovationFenetres} fenêtre(s)`,
                                    formData.renovationPortesFenetres > 0 && `${formData.renovationPortesFenetres} porte(s)-fenêtre(s)`,
                                    formData.renovationPorteEntree > 0 && `1 porte d'entrée`
                                  ].filter(Boolean).join(', ')}
                                </span>
                              </div>
                            )}
                            {formData.renovationAutreElement && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Autre élément :</span>
                                <span className="font-medium">{formData.renovationAutreElement}</span>
                              </div>
                            )}
                            {formData.renovationTimeline && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Délai souhaité :</span>
                                <span className="font-medium">
                                  {formOptions.renovationTimelines.find(t => t.value === formData.renovationTimeline)?.label || formData.renovationTimeline}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                        
                        {/* Service pour les demandes non-rénovation */}
                        {!isRenovationRequest && formData.serviceType && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Service :</span>
                            <span className="font-medium">
                              {formOptions.serviceTypes.find(t => t.value === formData.serviceType)?.label || '-'}
                            </span>
                          </div>
                        )}
                        
                        {formData.housingType && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Type de logement :</span>
                            <span className="font-medium">
                              {formOptions.housingTypes.find(t => t.value === formData.housingType)?.label || '-'}
                            </span>
                          </div>
                        )}
                        
                        {formData.isUrgent && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Urgence :</span>
                            <Badge className="bg-[hsl(24,95%,53%)] text-white">Oui</Badge>
                          </div>
                        )}
                      </div>

                      <div className="flex items-start gap-3 p-4 border rounded-lg">
                        <Checkbox 
                          id="consent" 
                          checked={formData.consent}
                          onCheckedChange={(checked) => updateFormData('consent', checked)}
                        />
                        <Label htmlFor="consent" className="text-sm cursor-pointer">
                          J'accepte d'être contacté(e) par NGL SERVICES pour le traitement de ma demande. 
                          Mes données sont traitées conformément à notre politique de confidentialité.
                        </Label>
                      </div>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  <div className="flex justify-between mt-8 pt-6 border-t">
                    {currentStep > 1 ? (
                      <Button variant="outline" onClick={prevStep} className="rounded-full">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Précédent
                      </Button>
                    ) : <div />}
                    
                    {currentStep < totalSteps ? (
                      <Button 
                        onClick={nextStep} 
                        disabled={!canProceed()}
                        className="btn-primary rounded-full"
                      >
                        Suivant
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleSubmit} 
                        disabled={!canProceed() || isSubmitting}
                        className="btn-secondary rounded-full"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Envoi en cours...
                          </span>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Envoyer ma demande
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Info */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-bold text-[hsl(215,25%,15%)] mb-4">Coordonnées</h3>
                  <div className="space-y-4">
                    <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[hsl(210,20%,98%)] transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center">
                        <Phone className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Téléphone</p>
                        <p className="font-semibold">{companyInfo.phone}</p>
                      </div>
                    </a>
                    <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[hsl(210,20%,98%)] transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center">
                        <Mail className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-semibold text-sm">{companyInfo.email}</p>
                      </div>
                    </a>
                    <div className="flex items-center gap-3 p-3">
                      <div className="w-10 h-10 rounded-full bg-[hsl(207,78%,95%)] flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Adresse</p>
                        <p className="font-semibold">{companyInfo.address}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency Card */}
              <Card className="border-0 shadow-lg bg-[hsl(24,95%,53%)] text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-8 h-8" />
                    <div>
                      <p className="font-bold text-lg">Urgences</p>
                      <p className="text-white/80 text-sm">24h/24 – 7j/7</p>
                    </div>
                  </div>
                  <p className="text-white/90 text-sm mb-4">
                    Porte claquée, serrure cassée, vitre brisée ? 
                    Appelez-nous immédiatement !
                  </p>
                  <a href={`tel:${companyInfo.phoneEmergency}`}>
                    <Button className="w-full bg-white text-[hsl(24,95%,53%)] hover:bg-gray-100 rounded-full font-bold">
                      <Phone className="w-4 h-4 mr-2" />
                      {companyInfo.phoneEmergency}
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <a href={`tel:${companyInfo.phoneEmergency}`} className="block">
                  <Button className="w-full emergency-btn rounded-full py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Être rappelé rapidement
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
